import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { contactApi } from '../services';
import logoService from '../services/logoService';
import {
  getAdvisorColor, getAdvisorInitials, contactAdvisors, industryLabel, peopleLabel, targetAccountLabel
} from './utils';
import './TreeView.css';

const PAGE_SIZE = 50;

const personInitials = (contact) => {
  const words = `${contact.first_name || ''} ${contact.last_name || ''}`.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
};

// Props for a clickable list row: activates on click, Enter and Space
const rowProps = (onActivate, selected) => ({
  role: 'button',
  tabIndex: 0,
  'aria-pressed': selected,
  onClick: onActivate,
  onKeyDown: (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onActivate();
    }
  }
});

const TreeView = ({
  theme,
  groupBy = 'industry',
  selectedIndustry,
  selectedAdvisor,
  companyNameFilter,
  contactNameFilter,
  positionFilter,
  onOpenCompany,
  onOpenPerson
}) => {
  const [industries, setIndustries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState({ industries: false, companies: false, contacts: false });
  const [error, setError] = useState({ industries: null, companies: null, contacts: null });
  const [companyPage, setCompanyPage] = useState({ page: 1, total: 0, hasMore: false });
  const [contactPage, setContactPage] = useState({ page: 1, total: 0, hasMore: false });

  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);

  // Latest request per column, so a slow earlier response cannot overwrite a newer one
  const companyRequest = useRef(0);
  const contactRequest = useRef(0);

  // Toolbar filters, applied to every column so counts match the graph
  const peopleFilters = useMemo(() => {
    const params = {};
    if (companyNameFilter) params.company_filter = companyNameFilter;
    if (contactNameFilter) params.contact_filter = contactNameFilter;
    if (positionFilter) params.position_filter = positionFilter;
    if (selectedAdvisor?.name) params.advisor_filter = selectedAdvisor.name;
    return params;
  }, [companyNameFilter, contactNameFilter, positionFilter, selectedAdvisor]);

  const activeFilterTags = [
    selectedAdvisor?.name && `Advisor: ${selectedAdvisor.name}`,
    companyNameFilter && `Company: ${companyNameFilter}`,
    contactNameFilter && `Name: ${contactNameFilter}`,
    positionFilter && `Position: ${positionFilter}`
  ].filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    setLoading(prev => ({ ...prev, industries: true }));
    setError(prev => ({ ...prev, industries: null }));

    contactApi.searchIndustryCompanyPeople({ ...peopleFilters, group_by: groupBy, max_companies: 1, limit: 100 })
      .then(overview => {
        if (cancelled) return;
        const mapped = (overview.data || []).map(industry => ({
          id: industry.name,
          name: industry.name,
          rank: industry.metadata?.rank,
          company_count: industry.metadata?.company_count || 0,
          contact_count: industry.metadata?.people_count || 0
        }));
        // ICP lists have their own significance order; plain industries don't, so alphabetize those
        mapped.sort(groupBy === 'icp'
          ? (a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity)
          : (a, b) => a.name.localeCompare(b.name));
        setIndustries(mapped);
      })
      .catch(err => {
        if (!cancelled) setError(prev => ({ ...prev, industries: err.message }));
      })
      .finally(() => {
        if (!cancelled) setLoading(prev => ({ ...prev, industries: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [peopleFilters, groupBy]);

  // Pre-select the industry or ICP chosen in the toolbar or from a graph "+N more" node
  useEffect(() => {
    setSelectedIndustries(selectedIndustry?.name ? [{ name: selectedIndustry.name }] : []);
    setSelectedCompanies([]);
    setSelectedContact(null);
  }, [selectedIndustry?.name, groupBy]);

  const loadCompanies = useCallback(async (page, reset) => {
    const requestId = ++companyRequest.current;
    setLoading(prev => ({ ...prev, companies: true }));
    setError(prev => ({ ...prev, companies: null }));

    try {
      const groupNames = selectedIndustries.map(industry => industry.name);
      const { companies: rows, pagination } = await contactApi.searchCompaniesByPeople({
        ...peopleFilters,
        ...(groupBy === 'icp' ? { group_by: 'icp', icp_filter: groupNames } : { industry_filter: groupNames }),
        page,
        limit: PAGE_SIZE
      });
      if (requestId !== companyRequest.current) return;
      setCompanies(prev => (reset ? rows : [...prev, ...rows]));
      setCompanyPage({ page, total: pagination.total_items, hasMore: page < pagination.total_pages });
    } catch (err) {
      if (requestId === companyRequest.current) {
        setError(prev => ({ ...prev, companies: err.message }));
      }
    } finally {
      if (requestId === companyRequest.current) {
        setLoading(prev => ({ ...prev, companies: false }));
      }
    }
  }, [peopleFilters, selectedIndustries, groupBy]);

  const loadContacts = useCallback(async (page, reset) => {
    const requestId = ++contactRequest.current;
    setLoading(prev => ({ ...prev, contacts: true }));
    setError(prev => ({ ...prev, contacts: null }));

    try {
      const { results, pagination } = await contactApi.searchContacts({
        ...peopleFilters,
        company_ids: selectedCompanies.map(company => company.id),
        dedupe: true,
        page,
        limit: PAGE_SIZE,
        sort: 'last_name',
        order: 'asc'
      });
      if (requestId !== contactRequest.current) return;
      setContacts(prev => (reset ? results : [...prev, ...results]));
      setContactPage({ page, total: pagination.total_items, hasMore: page < pagination.total_pages });
    } catch (err) {
      if (requestId === contactRequest.current) {
        setError(prev => ({ ...prev, contacts: err.message }));
      }
    } finally {
      if (requestId === contactRequest.current) {
        setLoading(prev => ({ ...prev, contacts: false }));
      }
    }
  }, [peopleFilters, selectedCompanies]);

  useEffect(() => {
    setCompanies([]);
    setCompanyPage({ page: 1, total: 0, hasMore: false });
    if (selectedIndustries.length > 0) {
      loadCompanies(1, true);
    } else {
      // Drop any response still in flight for the previous selection
      companyRequest.current += 1;
      setLoading(prev => ({ ...prev, companies: false }));
    }
  }, [selectedIndustries, loadCompanies]);

  useEffect(() => {
    setContacts([]);
    setContactPage({ page: 1, total: 0, hasMore: false });
    if (selectedCompanies.length > 0) {
      loadContacts(1, true);
    } else {
      contactRequest.current += 1;
      setLoading(prev => ({ ...prev, contacts: false }));
    }
  }, [selectedCompanies, loadContacts]);

  const handleIndustrySelect = (industry) => {
    setSelectedIndustries(prev => (
      prev.some(i => i.name === industry.name)
        ? prev.filter(i => i.name !== industry.name)
        : [...prev, { name: industry.name }]
    ));
    setSelectedCompanies([]);
    setSelectedContact(null);
  };

  const handleCompanySelect = (company) => {
    setSelectedCompanies(prev => (
      prev.some(c => c.id === company.id)
        ? prev.filter(c => c.id !== company.id)
        : [...prev, company]
    ));
    setSelectedContact(null);
    onOpenCompany(company);
  };

  // Highlights the row; the detail itself opens in the shared dock
  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    onOpenPerson(contact);
  };

  return (
    <div className={`tree-view ${theme}`}>
      <div className="tree-columns">

        <div className="tree-column industry-column">
          <div className="column-header">
            <h3>
              {groupBy === 'icp' ? 'ICP' : 'Industry'} ({industries.length})
              {selectedIndustries.length > 0 && (
                <span className="selected-count"> - {selectedIndustries.length} selected</span>
              )}
            </h3>
            {selectedIndustries.length > 0 && (
              <button
                className="clear-selection-button"
                onClick={() => {
                  setSelectedIndustries([]);
                  setSelectedCompanies([]);
                  setSelectedContact(null);
                }}
                title="Clear industry selection"
              >
                Clear Selection ({selectedIndustries.length})
              </button>
            )}
          </div>

          <div className="column-content">
            {loading.industries && (
              <div className="loading">
                <div className="loading-spinner"></div>
                <span>Loading industries...</span>
              </div>
            )}
            {error.industries && <div className="error">Error: {error.industries}</div>}

            {!loading.industries && !error.industries && (
              <div className="items-list">
                {industries.length === 0 && (
                  <div className="placeholder">No {groupBy === 'icp' ? 'ICPs' : 'industries'} match the filters</div>
                )}
                {industries.map(industry => {
                  const selected = selectedIndustries.some(i => i.name === industry.name);
                  return (
                    <div
                      key={industry.name}
                      className={`list-item ${selected ? 'selected' : ''}`}
                      {...rowProps(() => handleIndustrySelect(industry), selected)}
                    >
                      <div className="item-name">
                        {groupBy === 'icp' ? `${industry.rank ? `${industry.rank}. ` : ''}${industry.name}` : industryLabel(industry.name)}
                      </div>
                      <div className="item-meta">
                        <div className="count-item">
                          {industry.company_count} {industry.company_count === 1 ? 'company' : 'companies'}
                        </div>
                        <div className="count-item">
                          {peopleLabel(industry.contact_count)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="tree-column companies-column">
          <div className="column-header">
            <h3>
              Companies
              {selectedIndustries.length > 0 && (
                <span className="count-info"> ({companyPage.total})</span>
              )}
            </h3>
            {selectedIndustries.length > 0 && (
              <div className="loading-info">
                Showing {companies.length} of {companyPage.total}, {groupBy === 'icp' ? 'in list order' : 'most people first'}
              </div>
            )}
            {selectedCompanies.length > 0 && (
              <button
                className="unselect-all-button"
                onClick={() => {
                  setSelectedCompanies([]);
                  setSelectedContact(null);
                }}
                title="Unselect all companies"
              >
                Unselect All ({selectedCompanies.length})
              </button>
            )}
          </div>

          <div className="column-content">
            {selectedIndustries.length === 0 && (
              <div className="placeholder">Select one or more {groupBy === 'icp' ? 'ICPs' : 'industries'} to view companies</div>
            )}

            {loading.companies && companies.length === 0 && (
              <div className="loading">
                <div className="loading-spinner"></div>
                <span>Loading companies...</span>
              </div>
            )}
            {error.companies && <div className="error">Error: {error.companies}</div>}

            {selectedIndustries.length > 0 && !error.companies && companies.length > 0 && (
              <div className="items-list">
                {companies.map(company => {
                  const selected = selectedCompanies.some(c => c.id === company.id);
                  const logoUrl = logoService.getLogoUrl(company);
                  return (
                    <div
                      key={company.id}
                      className={`list-item ${selected ? 'selected' : ''}`}
                      {...rowProps(() => handleCompanySelect(company), selected)}
                    >
                      <div className="item-content">
                        <div className="item-logo">
                          {logoUrl ? (
                            <img
                              src={logoUrl}
                              alt=""
                              className="company-logo-small"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="company-initials" style={{display: logoUrl ? 'none' : 'flex'}}>
                            {company.name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="item-text">
                          <div className="item-name">{company.name}</div>
                          {company.target_account && (
                            <div className="item-meta">{targetAccountLabel(company.target_account)}</div>
                          )}
                          <div className="item-meta">
                            {peopleLabel(company.contact_count)}
                            {company.network_people > 0 && company.target_account ? ` (advisors know ${company.network_people})` : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {companyPage.hasMore && (
                  <button
                    className="load-more-button"
                    onClick={() => loadCompanies(companyPage.page + 1, false)}
                    disabled={loading.companies}
                  >
                    {loading.companies && (
                      <div className="button-spinner"></div>
                    )}
                    {loading.companies ? 'Loading...' : 'Load More Companies'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="tree-column contacts-column">
          <div className="column-header">
            <h3>
              People
              {selectedCompanies.length > 0 && (
                <span className="count-info"> ({contactPage.total})</span>
              )}
            </h3>
            {selectedCompanies.length > 0 && (
              <div className="loading-info">
                Showing {contacts.length} of {contactPage.total}
              </div>
            )}
            {activeFilterTags.length > 0 && (
              <div className="active-filters">
                <small>Filtered by:</small>
                {activeFilterTags.map(tag => <span key={tag} className="filter-tag">{tag}</span>)}
              </div>
            )}
          </div>

          <div className="column-content">
            {selectedCompanies.length === 0 && (
              <div className="placeholder">Select companies to view people</div>
            )}

            {loading.contacts && contacts.length === 0 && (
              <div className="loading">
                <div className="loading-spinner"></div>
                <span>Loading people...</span>
              </div>
            )}
            {error.contacts && <div className="error">Error: {error.contacts}</div>}

            {selectedCompanies.length > 0 && !error.contacts && contacts.length > 0 && (
              <div className="items-list">
                {contacts.map(contact => {
                  const advisors = contactAdvisors(contact);
                  const position = contact.position?.name || contact.position_name;
                  const company = contact.company_name || contact.company_detail?.name;
                  const selected = selectedContact?.id === contact.id;
                  return (
                    <div
                      key={contact.id}
                      className={`list-item contact-item ${selected ? 'selected' : ''}`}
                      {...rowProps(() => handleContactSelect(contact), selected)}
                      title={advisors.length ? `Known by ${advisors.join(', ')}` : undefined}
                    >
                      <div className="contact-avatar-container">
                        <div className="contact-avatar">
                          {personInitials(contact)}
                        </div>
                        {advisors.length > 0 && (
                          <div
                            className="advisor-bubble"
                            style={{ backgroundColor: getAdvisorColor(advisors[0]) }}
                          >
                            {getAdvisorInitials(advisors[0])}
                          </div>
                        )}
                        {advisors.length > 1 && (
                          <div className="advisor-bubble advisor-bubble--extra">
                            +{advisors.length - 1}
                          </div>
                        )}
                      </div>

                      <div className="contact-info">
                        <div className="contact-name">
                          {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unknown Contact'}
                          {contact.target_role && (
                            <span className="target-role-badge" title={`${contact.target_role === 'to' ? 'Main' : 'CC'} contact on a target account`}>
                              {contact.target_role === 'to' ? 'To' : 'CC'}
                            </span>
                          )}
                        </div>
                        {position && (
                          <div className="contact-position">{position}</div>
                        )}
                        {company && (
                          <div className="contact-company">{company}</div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {contactPage.hasMore && (
                  <button
                    className="load-more-button"
                    onClick={() => loadContacts(contactPage.page + 1, false)}
                    disabled={loading.contacts}
                  >
                    {loading.contacts && (
                      <div className="button-spinner"></div>
                    )}
                    {loading.contacts ? 'Loading...' : 'Load More People'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default TreeView;
